import { SearchInputWithRecent } from "@editor/components/search-input-with-recent";
import type {
  IMediaLibDialogItem,
  TElement,
} from "@editor/modules/media-lib/types";
import type { ComponentType, ReactNode } from "react";
import { useDebounceValue } from "usehooks-ts";
import { GiphyIcon } from "../../../icons";
import { GifsProvider } from "../ctx";
import { useFetchGifs, useResetStore } from "../hooks";
import { useGifsStoreShallow } from "../store";
import type { IGifs } from "../types";
import { GifsCategories } from "./gifs-categories";
import { GifsError } from "./gifs-error";
import { GifsNotFound } from "./gifs-not-found";
import { GifsSkeleton } from "./gifs-skeleton";
import { ImagesPanelGifsMasonry } from "./masonry";

export const Gifs = (context: {
  view?: "dialog" | "panel";
  onSelect?: (item: IGifs) => void;
  selectedItems?: IMediaLibDialogItem[];
  elementWrapper?: ComponentType<{ children: ReactNode; element: TElement }>;
  isMultiSelect?: boolean;
}) => {
  useResetStore();
  const { columnsData, queryRaw, setQueryRaw } = useGifsStoreShallow((s) => ({
    columnsData: s.columnsData,
    queryRaw: s.queryRaw,
    setQueryRaw: s.setQueryRaw,
  }));

  const [searchQuery, setDebouncedSearchQuery] = useDebounceValue(
    queryRaw,
    500,
  );

  const { isError, reloadData, shouldShowSkeleton, fetchMoreGifs } =
    useFetchGifs({
      searchQuery,
    });

  const isBufferingSerach = searchQuery !== queryRaw;
  return (
    <GifsProvider {...context}>
      <div className="tw-relative tw-grid tw-h-full tw-grid-rows-[auto_1fr] tw-overflow-hidden">
        <div className="tw-grid tw-max-w-full tw-px-6">
          <SearchInputWithRecent
            placeholder="Search GIFs"
            value={queryRaw}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length === 0) {
                setDebouncedSearchQuery(value);
              }
              setQueryRaw(value);
            }}
            onSubmit={(value) => {
              setDebouncedSearchQuery(value);
              setQueryRaw(value);
            }}
            onClear={() => {
              setDebouncedSearchQuery("");
              setQueryRaw("");
            }}
            isProcessing={isBufferingSerach}
            storageKey="sd-images-panel-gifs-recent-search"
            amountOfStoredSearches={3}
          />

          {searchQuery.length === 0 && <GifsCategories />}
        </div>

        {isError ? (
          <GifsError onReload={reloadData} />
        ) : shouldShowSkeleton ? (
          <GifsSkeleton />
        ) : columnsData[0].length === 0 ? (
          <GifsNotFound />
        ) : (
          <ImagesPanelGifsMasonry
            searchQuery={searchQuery}
            loadMoreGifs={fetchMoreGifs}
          />
        )}
        <div
          className="tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-h-8 tw-max-w-24 tw-items-center tw-justify-center tw-rounded-[40px] tw-bg-white tw-px-3 tw-py-2 tw-transition-colors"
          style={{
            boxShadow:
              "0px 0px 0px 1px rgba(18, 3, 62, 0.06), 0px 5px 1px 0px rgba(18, 3, 62, 0.00), 0px 3px 1px 0px rgba(18, 3, 62, 0.01), 0px 2px 1px 0px rgba(18, 3, 62, 0.03), 0px 1px 1px 0px rgba(18, 3, 62, 0.04)",
          }}
        >
          <GiphyIcon className="tw-size-auto" />
        </div>
      </div>
    </GifsProvider>
  );
};
